import * as React from "react";
import "./HTMLTooltip.component.css";
import { useEffect, useRef, useState } from "react";
let timeoutHandle;
const tooltipInterval = 500;
// We cannot use the exact mouse position to display the tooltip for 2 reasons:
// 1. The Cursor would cover the tooltip
// 2. The mouse would enter the tooltip and leave the element with the tooltip, which would hide the tooltip (and show it again, and hide it again, and so on)
// The offsets x:10 and y:15 move the tooltip to the same position as chrome uses for default tooltips (e.g. those set with the title attribute)
const tooltipOffsetX = 10;
const tooltipOffsetY = 15;
const validTooltipPositions = ["bottom-right", "bottom-left", "top-right", "top-left"];
const attrTooltip = "data-tooltip";
const attrTooltipDisabled = "data-tooltip-disabled";
const attrTooltipPosition = "data-tooltip-position";
const attrTooltipWrapper = "data-tooltip-wrapper";
/**
 * Usage:
 * Just place an instance of this component in app.component and it will automatically add tooltips to all elements with the "data-tooltip" and/or "data-tooltip-disabled" attributes.
 *
 * Important:
 * This will only work for standard HTML elements (e.g. div, button, input, etc.). It will not work for custom components.
 * For custom components, you have 2 options:
 * - Add the tooltip inside the components jsx and provide a property for the text if needed. (See MenuIconButton for an example)
 * - Use the "data-tooltip" attribute on a wrapper div around the custom component.
 *   "data-tooltip-wrapper" needs to be set on the wrapper div if "data-tooltip-disabled" is needed.
 *   Best use HTMLTooltipWrapper instead of a plain div, it handles everything. (See SessionSelect for an example)
 *
 * By default, the tooltip will be displayed below and right of the cursor.
 * You can change this by adding the "data-tooltip-position" attribute to the element with the tooltip.
 * Valid values are: "bottom-right" (default), "bottom-left", "top-right", "top-left"
 *
 * Behaviour of "data-tooltip" vs. "data-tooltip-disabled":
 *
 * - The element only has the "data-tooltip" attribute:
 *   "data-tooltip" will always be displayed, regardless of the disabled-state.
 *
 * - The element has both the "data-tooltip" and the "data-tooltip-disabled" attribute:
 *   "data-tooltip-disabled" will be displayed if the element is disabled, otherwise "data-tooltip" will be displayed.
 *
 * - The element only has the "data-tooltip-disabled" attribute:
 *   "data-tooltip-disabled" will be displayed when the element has the disabled attribute, and no tooltip will be displayed otherwise.
 *
 * Hint:
 * You might encounter problems with "data-tooltip-disabled" when using HTMLTooltipWrapper around a complex component that is composed of several elements,
 * because "data-tooltip-disabled" will be shown as soon as one descendant element has the disabled attribute. (Not only direct children)
 * In this case, the best solution would be to not use "data-tooltip-disabled" at all:
 * Use "data-tooltip" instead and manually change the text depending on the custom disabled/enabled state logic.
 *
 * Formatting:
 * The only formatting currently supported is line breaks.
 * Use "\n" to insert a line break.
 * It seems like the string have to be written as data-tooltip={`line1\nline2`} instead of data-tooltip="line1\nline2" to make linebreaks work.
 */
export function HTMLTooltip() {
    const [text, setText] = useState("");
    const [visible, setVisible] = useState(false);
    const [origin, setOrigin] = useState({
        x: 0,
        y: 0
    });
    const [position, setPosition] = useState("bottom-right");
    const visibleRef = useRef(visible);
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const elementsWithTooltip = document.querySelectorAll(`[${attrTooltip}], [${attrTooltipDisabled}]`);
            elementsWithTooltip.forEach((element) => {
                element.removeEventListener("mouseenter", mouseEnterHandler);
                element.removeEventListener("mouseleave", mouseLeaveHandler);
                element.removeEventListener("mousemove", mouseMoveHandler);
                element.removeEventListener("click", mouseLeaveHandler);
                element.removeEventListener("keydown", mouseLeaveHandler);
                element.addEventListener("mouseenter", mouseEnterHandler);
                element.addEventListener("mouseleave", mouseLeaveHandler);
                element.addEventListener("mousemove", mouseMoveHandler);
                element.addEventListener("click", mouseLeaveHandler);
                element.addEventListener("keydown", mouseLeaveHandler);
            });
        });
        observer.observe(document, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        visibleRef.current = visible;
    }, [visible]);
    const isElementDisabled = (element) => {
        if (element.hasAttribute("disabled")) {
            return true;
        }
        if (element.hasAttribute(attrTooltipWrapper)) {
            const stack = Array.from(element.children);
            while (stack.length) {
                const child = stack.pop();
                if (child.hasAttribute("disabled")) {
                    return true;
                }
                stack.push(...Array.from(child.children));
            }
        }
        return false;
    };
    const mouseEnterHandler = (event) => {
        var _a;
        const htmlElement = event.currentTarget;
        if (!htmlElement) {
            return;
        }
        let tooltipText = htmlElement.getAttribute(attrTooltip);
        const tooltipDisabled = htmlElement.getAttribute(attrTooltipDisabled);
        if (tooltipDisabled && isElementDisabled(htmlElement)) {
            tooltipText = tooltipDisabled;
        }
        if (tooltipText) {
            let tooltipPosition = (_a = htmlElement.getAttribute(attrTooltipPosition)) !== null && _a !== void 0 ? _a : "";
            if (!validTooltipPositions.includes(tooltipPosition)) {
                tooltipPosition = "bottom-right";
            }
            setText(tooltipText);
            setPosition(tooltipPosition);
            setOrigin({ x: event.clientX, y: event.clientY });
            setVisibleDelayed();
        }
    };
    const mouseMoveHandler = (event) => {
        if (timeoutHandle) {
            setOrigin({ x: event.clientX, y: event.clientY });
            setVisibleDelayed();
        }
        else if (!visibleRef.current) {
            mouseEnterHandler(event);
        }
    };
    const mouseLeaveHandler = () => {
        if (timeoutHandle) {
            window.clearTimeout(timeoutHandle);
            timeoutHandle = undefined;
        }
        setVisible(false);
        setText("");
    };
    const setVisibleDelayed = () => {
        window.clearTimeout(timeoutHandle);
        timeoutHandle = window.setTimeout(() => {
            timeoutHandle = undefined;
            setVisible(true);
        }, tooltipInterval);
    };
    const getStyleObject = () => {
        let result = {};
        switch (position) {
            default:
            case "bottom-right":
                result = {
                    left: origin.x + tooltipOffsetX,
                    top: origin.y + tooltipOffsetY
                };
                break;
            case "bottom-left":
                result = {
                    right: window.innerWidth - origin.x + tooltipOffsetX,
                    top: origin.y + tooltipOffsetY
                };
                break;
            case "top-right":
                result = {
                    left: origin.x + tooltipOffsetX,
                    bottom: window.innerHeight - origin.y + tooltipOffsetY
                };
                break;
            case "top-left":
                result = {
                    right: window.innerWidth - origin.x + tooltipOffsetX,
                    bottom: window.innerHeight - origin.y + tooltipOffsetY
                };
                break;
        }
        result = Object.assign(Object.assign({}, result), { position: "absolute", zIndex: 9999999 });
        return result;
    };
    const getTooltipText = () => {
        if (!text) {
            return "";
        }
        const lines = text.split("\n");
        return lines.map((line, index) => {
            if (index < lines.length - 1) {
                return (React.createElement("span", { key: index },
                    line,
                    React.createElement("br", null)));
            }
            else {
                return React.createElement("span", { key: index }, line);
            }
        });
    };
    return (React.createElement(React.Fragment, null, visible && (React.createElement("div", { className: "tooltip-container", style: getStyleObject() }, getTooltipText()))));
}
